// ShowCustomerInfo.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getRestaurantInfoClient } from "../actions/userActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import ReservationScreen from "../screens/ReservationScreen";
import ReservationCard from "./ReservationCard";
import { CustomModal } from "./CustomModal";

const primaryText = "#99b0ad";
const softBackground = "#FAF6E9";

const StyledUserIcon = styled(FontAwesomeIcon).attrs({ icon: faUserCircle })`
  font-size: 50px;
  color: ${primaryText};
  background-color: ${softBackground};
  border-radius: 50%;
`;

const UserInfoSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 5px;
`;

const Name = styled.h3`
  font-size: 19px;
  font-weight: 600;
  margin: 6px 0 2px;
  color: ${primaryText};
`;

const PhoneNumber = styled.p`
  font-size: 13px;
  margin: 0;
  color: #666;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #888888ff;
  width: 100%;
  margin: 14px 0 12px;
`;

const SectionTitle = styled.h5`
  font-size: 17px;
  font-weight: 500;
  text-align: center;
  margin-bottom: 10px;
  color: ${primaryText};
`;

const ReservationsList = styled.div`
  height: 300px;
  overflow-y: auto;
  margin-top: 12px;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
  -ms-overflow-style: auto;
`;

/* Search + Pagination UI (mirrors your other screen) */
const SearchContainer = styled.div`
  max-width: 420px;
  margin: 10px auto 14px;
  position: relative;
`;
const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 14px;
  border-radius: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  font-size: 14px;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: #ee754c;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.15);
  }
`;
const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
`;

const FixedPagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin: 10px 0 4px;
`;
const PageSide = styled.div`
  display: flex;
  justify-content: center;
  min-width: 120px;
`;
const PageCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const PageButton = styled.div`
  background-color: #f2f2f2;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  &:hover {
    background-color: #e0e0e0;
  }
`;
const PageNumber = styled.div`
  background-color: #ee754c;
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  text-align: center;
  min-width: 40px;
`;
const PagePlaceholder = styled.div`
  visibility: hidden;
  min-width: 90px;
`;

function ShowCustomerInfo({
  phone_number,
  restaurant_id,
  isModalActive,
  setIsModalActive,
}) {
  const dispatch = useDispatch();

  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [tokens, setTokens] = useState("");
  const [reset, setReset] = useState(false);

  // NEW: search + pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const clientInfo = useSelector((state) => state.restaurant_clients);
  const {
    restaurantInfoClients,
    isLoadingGetRestaurantInfoClient,
    errorGetRestaurantInfoClient,
  } = clientInfo;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // reset to page 1 on new search
    }, 600);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset search + page when a new client opens
  useEffect(() => {
    if (isModalActive) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setCurrentPage(1);
    }
  }, [isModalActive, phone_number]);

  // Fetch data whenever dependencies change
  useEffect(() => {
    if (phone_number && restaurant_id) {
      dispatch(
        getRestaurantInfoClient(
          phone_number,
          restaurant_id,
          currentPage,
          debouncedSearchTerm
        )
      );
    }
  }, [dispatch, phone_number, restaurant_id, currentPage, debouncedSearchTerm]);

  const closeModal = () => setIsModalActive(false);

  const handleCloseReservationModal = () => {
    setIsReservationModalOpen(false);
  };

  const handleOpenEditReservationModal = (tokeni) => {
    setIsReservationModalOpen(true);
    setIsCreatingReservation(false);
    setTokens(tokeni);
  };

  // Read paginated data safely (supports new + old API shape)
  const totalResults =
    restaurantInfoClients?.reservations?.total_results ??
    (Array.isArray(restaurantInfoClients?.reservations)
      ? restaurantInfoClients.reservations.length
      : 0);

  const PAGE_SIZE = 10;

  const totalPages =
    restaurantInfoClients?.reservations?.total_pages ??
    (Math.ceil((totalResults || 0) / PAGE_SIZE) || 0); // grouped right side

  const pageResults = Array.isArray(restaurantInfoClients?.reservations)
    ? restaurantInfoClients.reservations
    : restaurantInfoClients?.reservations?.results ?? [];
  const memoizedReservations = useMemo(() => pageResults, [pageResults]);

  const handlePrevPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);
  const handleNextPage = () =>
    currentPage < totalPages && setCurrentPage((p) => p + 1);

  return (
    <>
      <CustomModal show={isModalActive} onHide={closeModal} title="Cliente">
        <UserInfoSection>
          <StyledUserIcon />

          {isLoadingGetRestaurantInfoClient && <p>Carregando...</p>}

          {restaurantInfoClients && (
            <>
              <Name>{restaurantInfoClients.clientInfo.client.name}</Name>
              {restaurantInfoClients.clientInfo.client.phone_number.length >
              4 ? (
                <PhoneNumber>
                  +
                  {restaurantInfoClients.clientInfo.client.phone_number.slice(
                    0,
                    3
                  )}{" "}
                  {restaurantInfoClients.clientInfo.client.phone_number.slice(
                    3
                  )}
                </PhoneNumber>
              ) : (
                <PhoneNumber>Sem número</PhoneNumber>
              )}
            </>
          )}
        </UserInfoSection>

        <Divider />
        <SectionTitle>Histórico de reservas</SectionTitle>

        {/* Search like the first screen */}
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Procurar nas reservas por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon icon={faSearch} />
        </SearchContainer>

        {/* Pagination header (optional, like your first screen) */}
        {totalResults > 0 && (
          <FixedPagination>
            <PageSide>
              {currentPage > 1 ? (
                <PageButton onClick={handlePrevPage}>← Anterior</PageButton>
              ) : (
                <PagePlaceholder />
              )}
            </PageSide>
            <PageCenter>
              <PageNumber>{currentPage}</PageNumber>
            </PageCenter>
            <PageSide>
              {currentPage < totalPages ? (
                <PageButton onClick={handleNextPage}>Próxima →</PageButton>
              ) : (
                <PagePlaceholder />
              )}
            </PageSide>
          </FixedPagination>
        )}

        <ReservationsList>
          {restaurantInfoClients && (
            <ReservationCard
              reservations={memoizedReservations}
              isShowCustomerInfo={true}
              handleEdit={handleOpenEditReservationModal}
            />
          )}
          {!isLoadingGetRestaurantInfoClient &&
            memoizedReservations.length === 0 && (
              <p className="text-center mt-3">Nenhuma reserva encontrada.</p>
            )}
        </ReservationsList>
      </CustomModal>

      <Modal
        show={isReservationModalOpen}
        onHide={handleCloseReservationModal}
        centered
        size="lg"
      >
        <ReservationScreen
          from_restaurant={true}
          creating={isCreatingReservation}
          setCreating={setIsCreatingReservation}
          isEditModalOpen={isReservationModalOpen}
          setIsEditModalOpen={setIsReservationModalOpen}
          restaurantId={restaurant_id}
          handleCloseReservationModal={handleCloseReservationModal}
          token_from_restaurant={tokens}
          reset={reset}
        />
      </Modal>
    </>
  );
}

export default ShowCustomerInfo;
